import {
  TaskShotBidStatus,
  BidPackageAction,
  TaskShot,
} from "@/__generated__/graphql";
import {
  STATUS_LIST_IN_PROGRESS,
  STATUS_LIST_COMPLETED,
  STATUS_LIST_ASSIGNED,
  STATUS_LIST_AWARDED,
  STATUS_LIST_CANCELLABLE,
} from "@/helpers/constants";
import { Deliveries } from "@/pages/Assigned/types";
import { orderBy } from "lodash";

export interface Vendor {
  __typename?: "Company";
  id: string;
}

export interface TaskShotBid {
  __typename?: "TaskShotBid";
  id: string;
  deliveryDate: string | null;
  status: TaskShotBidStatus;
  vendor: Vendor;
  currentBidPackage?: BidPackage;
}

export interface BidPackage {
  __typename?: "BidPackage";
  id: string;
  action: BidPackageAction;
}

export const isAwarded = (tsb: TaskShotBid) =>
  !!tsb.status && STATUS_LIST_AWARDED.indexOf(tsb.status) !== -1;

export const isAssigned = (tsb: TaskShotBid) =>
  !!tsb.status && STATUS_LIST_ASSIGNED.indexOf(tsb.status) !== -1;

export const isAwardedInPackage = (bidPackage: BidPackage) =>
  bidPackage.action === BidPackageAction.PendingBid ? isAssigned : isAwarded;

export const isNotPending = (tsb: TaskShotBid) =>
  !!tsb.status && tsb.status !== TaskShotBidStatus.Pending;

export const isPendingStateChange = (tsb: TaskShotBid) =>
  !!tsb.status && tsb.status === TaskShotBidStatus.PendingStateChange;

export const isAwardedStateChange = (
  taskShotBid: TaskShotBid,
  bidPackage: BidPackage,
) =>
  // Is already in a different bid package
  !!(
    (taskShotBid.currentBidPackage &&
      taskShotBid.currentBidPackage.id !== bidPackage.id) ||
    // Is in this bid package, but not pending
    (taskShotBid.currentBidPackage &&
      taskShotBid.currentBidPackage.id === bidPackage.id &&
      !isPendingStateChange(taskShotBid))
  );

export const isInProgress = (tsb: TaskShotBid) =>
  !!tsb.status && STATUS_LIST_IN_PROGRESS.indexOf(tsb.status) !== -1;

export const isCompleted = (tsb: TaskShotBid) =>
  !!tsb.status && STATUS_LIST_COMPLETED.indexOf(tsb.status) !== -1;

export const isCancelable = (tsb: TaskShotBid) =>
  !!tsb.status && STATUS_LIST_CANCELLABLE.indexOf(tsb.status) !== -1;

export const isCancelled = (tsb: TaskShotBid) =>
  !!tsb.status && tsb.status === TaskShotBidStatus.Cancelled;

export const isInvoiced = (tsb: TaskShotBid) =>
  !!tsb.status && tsb.status === TaskShotBidStatus.Invoiced;

export const getShotName = (
  taskShots: TaskShot[],
  taskShotBid: TaskShotBid,
) => {
  const taskShot = taskShots.find((ts) =>
    ts.bids.find((bid) => bid.id === taskShotBid.id),
  );
  return taskShot ? taskShot.shot.name : "";
};
export const getShotNameForVendorBids = (
  taskShots: TaskShot[],
  taskShotBid: TaskShotBid,
) => {
  const taskShot = taskShots.find((ts) =>
    ts?.vendorBids?.find((bid) => bid.id === taskShotBid.id),
  );
  return taskShot ? taskShot.shot.name : "";
};
export const getOrderedDeliveries = (
  deliveries: Deliveries[],
): Deliveries[] => {
  const cleaned = deliveries
    .map((delivery) => ({
      ...delivery,
      deliveryFilesToSourceTaskShotIds:
        delivery.deliveryFilesToSourceTaskShotIds.map(
          ({ __typename, ...keep }) => keep,
        ),
    }))
    .filter((del) => del.deliveryFilesToSourceTaskShotIds.length > 0);

  return orderBy(cleaned, (del) => del.created, "desc");
};
